import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Calendar, MapPin, ExternalLink, Share2, Copy, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import SafeImage from "../components/SafeImage";
import AiPhotoBadge from "../components/AiPhotoBadge";
import SEO from "../components/SEO";
import Breadcrumbs from "../components/Breadcrumbs";
import { CATEGORY_LABELS } from "../components/FeedCard";
import { trackEvent, EV } from "../lib/analytics";

const SITE = "https://guiadejampa.com";

function fmtDate(d) {
  if (!d) return "";
  try { return new Date(d).toLocaleDateString("pt-BR", { day: "2-digit", month: "long", year: "numeric" }); }
  catch { return ""; }
}

export default function NewsDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true); setNotFound(false);
    api.get(`/news/${slug}`)
      .then(({ data }) => {
        if (!active) return;
        setPost(data.post); setRelated(data.related || []);
        trackEvent(EV.NEWS_VIEW, { slug: data.post?.slug, category: data.post?.category });
      })
      .catch(() => { if (active) setNotFound(true); })
      .finally(() => { if (active) setLoading(false); });
    window.scrollTo({ top: 0 });
    return () => { active = false; };
  }, [slug]);

  const shareUrl = `${window.location.origin}/api/share/noticia/${slug}`;
  const shareText = post ? `Confira esta notícia no Guia de Jampa AI: ${post.title}` : "";
  const shareWhatsApp = () => {
    trackEvent(EV.SHARE_CLICK, { type: "news", channel: "whatsapp", slug });
    window.open(`https://wa.me/?text=${encodeURIComponent(`${shareText} ${shareUrl}`)}`, "_blank", "noopener,noreferrer");
  };
  const copyLink = async () => {
    trackEvent(EV.SHARE_CLICK, { type: "news", channel: "copy", slug });
    try { await navigator.clipboard.writeText(shareUrl); toast.success("Link copiado!"); }
    catch { toast.error("Não foi possível copiar o link"); }
  };
  const nativeShare = async () => {
    trackEvent(EV.SHARE_CLICK, { type: "news", channel: "native", slug });
    try {
      if (navigator.share) await navigator.share({ title: post.title, text: shareText, url: shareUrl });
      else copyLink();
    } catch (e) {
      if (e?.name !== "AbortError" && process.env.NODE_ENV !== "production") console.debug("Share failed:", e);
    }
  };

  if (loading) return <div className="py-24 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;
  if (notFound || !post) return (
    <div className="py-20 text-center" data-testid="news-notfound">
      <SEO title="Notícia não encontrada" path={`/noticia/${slug}`} noindex />
      <p className="text-muted-foreground">Notícia não encontrada.</p>
      <Link to="/feed" className="text-primary font-semibold text-sm">Voltar ao feed</Link>
    </div>
  );

  const body = post.content && post.content !== post.summary ? post.content : post.summary;
  const seo = post.seo || {};
  const published = post.published_at || post.date || post.created_at || "";
  const modified = post.updated_at || post.captured_at || published;
  const image = post.image || seo.image;
  const authorName = seo.author || post.author_name || (post.guia_news_ai ? "Guia News AI" : "Redação Guia de Jampa AI");
  const isOrg = seo.guia_news_ai || post.guia_news_ai;
  const wordCount = seo.word_count || (body ? body.split(/\s+/).length : 0);
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "NewsArticle",
    headline: post.title,
    description: seo.meta_description || post.summary,
    ...(image ? { image: [image] } : {}),
    datePublished: published,
    dateModified: modified,
    articleSection: seo.article_section || post.category,
    ...(seo.keywords?.length ? { keywords: seo.keywords.join(", ") } : {}),
    ...(wordCount ? { wordCount } : {}),
    inLanguage: "pt-BR",
    author: { "@type": isOrg ? "Organization" : "Person", name: authorName },
    publisher: { "@type": "Organization", name: "Guia de Jampa AI",
                 logo: { "@type": "ImageObject", url: `${SITE}/logo-h.png` } },
    mainEntityOfPage: `${SITE}/noticia/${post.slug}`,
    ...(post.original_source_url ? { isBasedOn: post.original_source_url } : {}),
  };

  return (
    <motion.article data-testid="news-detail" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
      className="max-w-3xl mx-auto pb-16">
      <SEO
        title={post.title}
        description={seo.meta_description || post.summary}
        path={`/noticia/${post.slug}`}
        image={image}
        type="article"
        jsonLd={jsonLd}
        keywords={(seo.keywords || []).join(", ") || undefined}
      />
      <button data-testid="news-back" onClick={() => navigate(-1)}
        className="flex items-center gap-2 text-sm text-muted-foreground mb-4 hover:text-primary transition-colors">
        <ArrowLeft className="w-4 h-4" /> Voltar
      </button>

      <Breadcrumbs items={[
        { name: "Notícias", to: "/feed?category=noticias" },
        { name: post.title.length > 60 ? post.title.slice(0, 57) + "…" : post.title },
      ]} />

      <div className="relative rounded-3xl overflow-hidden border border-border mt-3">
        <SafeImage src={post.image} category={post.category} alt={post.title} className="w-full h-64 md:h-96 object-cover bg-muted" />
        {post.ai_image && <AiPhotoBadge />}
      </div>

      <div className="mt-5">
        <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
          <span className="rounded-full bg-primary/10 text-primary font-semibold px-3 py-1">{CATEGORY_LABELS[post.category] || post.category}</span>
          {(post.published_at || post.date) && <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {fmtDate(post.published_at || post.date)}</span>}
          <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {post.location || post.city || "João Pessoa"}</span>
        </div>

        <h1 className="font-heading font-bold text-3xl md:text-4xl leading-tight mt-3">{post.title}</h1>

        {post.summary && body !== post.summary && (
          <p className="text-lg text-muted-foreground mt-4 leading-relaxed">{post.summary}</p>
        )}

        <div className="prose prose-lg max-w-none mt-4 text-foreground/90 leading-relaxed whitespace-pre-wrap">{body}</div>

        <div className="flex flex-wrap items-center gap-2 mt-6">
          <span className="text-sm font-semibold text-muted-foreground mr-1">Compartilhar:</span>
          <button data-testid="share-whatsapp" onClick={shareWhatsApp}
            className="flex items-center gap-2 rounded-full bg-[#25D366] text-white text-sm font-semibold px-4 py-2 active:scale-95 transition-transform">
            <Share2 className="w-4 h-4" /> WhatsApp
          </button>
          <button data-testid="share-copy" onClick={copyLink}
            className="flex items-center gap-2 rounded-full border border-border text-sm font-semibold px-4 py-2 active:scale-95 transition-transform">
            <Copy className="w-4 h-4" /> Copiar link
          </button>
          <button data-testid="share-native" onClick={nativeShare}
            className="flex items-center gap-2 rounded-full border border-border text-sm font-semibold px-4 py-2 active:scale-95 transition-transform">
            <Share2 className="w-4 h-4" /> Compartilhar
          </button>
        </div>

        <div className="mt-8 rounded-2xl border border-border bg-card p-4">
          <p className="text-sm text-muted-foreground">Fonte original: <b className="text-foreground">{post.source_name || post.source}</b></p>
          {(post.original_url || post.link) && (
            <a data-testid="news-source-link" href={post.original_url || post.link} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 mt-2 text-sm font-semibold text-primary">
              <ExternalLink className="w-4 h-4" /> Ver publicação original
            </a>
          )}
        </div>
      </div>

      {related.length > 0 && (
        <div className="mt-10">
          <h2 className="font-heading font-bold text-lg mb-4">Notícias relacionadas</h2>
          <div className="grid sm:grid-cols-2 gap-4" data-testid="news-related">
            {related.map((r) => (
              <button key={r.id} data-testid={`related-${r.id}`} onClick={() => navigate(`/noticia/${r.slug}`)}
                className="text-left rounded-2xl overflow-hidden border border-border bg-card hover:border-primary transition-colors">
                <SafeImage src={r.image} category={r.category} alt={r.title} className="w-full h-36 object-cover bg-muted" />
                <div className="p-3">
                  <span className="text-[11px] text-primary font-semibold">{CATEGORY_LABELS[r.category] || r.category}</span>
                  <p className="font-heading font-semibold text-sm leading-snug mt-1 line-clamp-2">{r.title}</p>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </motion.article>
  );
}
